/**
 * Cart Notification Component
 * Handles the display and interaction of cart notification popup
 */
class CartNotification extends HTMLElement {
  constructor() {
    super();
    
    this.notification = document.getElementById('cart-notification');
    this.activeElement = null;
    // Bind once so the same function reference is used for add and remove.
    this.handleBodyClick = this.handleBodyClick.bind(this);

    if (!this.notification) {
      console.warn('Cart notification element not found');
      return;
    }

    this.initializeEventListeners();
  }
  
  /**
   * Initialize all event listeners
   */
  initializeEventListeners() {
    // Escape key to close
    this.notification.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });
    
    // Close button clicks
    this.querySelectorAll('button[type="button"]').forEach((button) => {
      button.addEventListener('click', this.close.bind(this));
    });
  }
  
  /**
   * Show notification with smooth animation
   */
  open() {
    this.notification.classList.remove('hidden');
    this.notification.classList.add('opacity-100', 'translate-y-0');
    this.notification.classList.remove('opacity-0', 'translate-y-2');
    
    // Focus and trap focus after animation
    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        if (typeof trapFocus === 'function') {
          trapFocus(this.notification);
        }
      },
      { once: true }
    );
    
    // Add backdrop click listener
    document.body.addEventListener('click', this.handleBodyClick);
  }
  
  /**
   * Hide notification with smooth animation
   */
  close() {
    this.notification.classList.add('opacity-0', 'translate-y-2');
    this.notification.classList.remove('opacity-100', 'translate-y-0');
    
    // Hide completely after transition
    setTimeout(() => {
      this.notification.classList.add('hidden');
    }, 200);
    
    // Remove backdrop click listener
    document.body.removeEventListener('click', this.handleBodyClick);
    
    // Remove focus trap
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
  }
  
  /**
   * Render cart notification contents
   */
  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    
    // Update sections
    this.getSectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      if (element && parsedState.sections[section.id]) {
        element.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      }
    });
    
    // Update cart button text
    this.updateCartButtonText();

    this.open();
  }

  /**
   * Reset the cart button text after the section swap
   */
  updateCartButtonText() {
    const cartButton = document.getElementById('cart-notification-button');
    if (cartButton) {
      cartButton.textContent = 'Review Bag';
    }
  }

  /**
   * Get sections that need to be updated
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
    ];
  }
  
  /**
   * Extract HTML content from section response
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const element = doc.querySelector(selector);
      return element ? element.innerHTML : '';
    } catch (error) {
      console.error('Error parsing section HTML:', error);
      return '';
    }
  }
  
  /**
   * Handle backdrop clicks to close notification
   */
  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }
  
  /**
   * Set the active element for focus management
   */
  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
  